import { Trans, useTranslation } from '@repo/translations/use-translation';

import { TextBadge } from '@zooplus/ui-components';

import { OfferPrice } from '../../../UI/OfferPrice';
import { ProgramCardIcon } from '../../../UI/ProgramCardIcon/ProgramCardIcon';
import { ProgramLogo } from '../../../UI/ProgramLogo/ProgramLogo';

import styles from './ProgramPlanHeader.module.css';

import type { MembershipsComparison } from '@repo/types/memberships-comparison';

export interface ProgramPlanHeaderProps {
  membershipsComparison: MembershipsComparison;
  plan: 'BASIC' | 'PREMIUM';
}

export const ProgramPlanHeader = ({ membershipsComparison, plan }: ProgramPlanHeaderProps) => {
  const { t } = useTranslation();

  const isCurrentPlan = () => {
    switch (membershipsComparison.customerType) {
      case 'basic':
        return plan === 'BASIC';
      case 'premium':
        return plan === 'PREMIUM';
      default:
        return false;
    }
  };

  return (
    <div className={styles.planHeader}>
      <div className={styles.logoAndPlanContainer}>
        <ProgramLogo height={19} />
        <h2 className={styles.plan}>
          {plan === 'BASIC'
            ? t('loyalty-program.public-landing-page.program-plans.header.basic-level')
            : t('loyalty-program.public-landing-page.program-plans.header.premium-level')}
        </h2>
      </div>
      <ProgramCardIcon
        level={plan === 'PREMIUM' ? 'premium' : 'basic'}
        variant="rotated"
        className={styles.card}
        data-testid={plan === 'PREMIUM' ? 'premium-card' : 'basic-card'}
      />
      <div className={styles.priceContainer}>
        {plan === 'BASIC' ? (
          <span className={styles.price}>
            {t('loyalty-program.public-landing-page.program-plans.header.level-price-free')}
          </span>
        ) : (
          <span data-testid={'price-per-year'} className={styles.yearlyPrice}>
            <Trans
              i18nKey={'loyalty-program.public-landing-page.program-plans.header.yearly-price'}
              components={{
                Price: (
                  <OfferPrice
                    price={
                      membershipsComparison.premiumLoyaltyMembershipInfo.membershipPrice?.price
                    }
                    referencePrice={
                      membershipsComparison.premiumLoyaltyMembershipInfo.membershipPrice
                        ?.referencePrice
                    }
                    secondaryCurrencyPrice={
                      membershipsComparison.premiumLoyaltyMembershipInfo.membershipPrice
                        ?.secondaryCurrencyPrice
                    }
                    size="large"
                  />
                ),
              }}
            />
          </span>
        )}
        {isCurrentPlan() && (
          <TextBadge
            className={styles.currentPlan}
            label={t('loyalty-program.public-landing-page.program-plans.current-plan')}
            variant="info"
          />
        )}
      </div>
    </div>
  );
};
